import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { localizedPath } from '../routes/pathHelpers';
import { Language, RouteKey, RouteParams } from '../routes/routeConfig';
import { SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from '../config/seo';

interface SEOConfig {
  titleKey: string;
  descriptionKey: string;
  routeKey: RouteKey;
  routeParams?: RouteParams;
  titleValues?: Record<string, string>;
  descriptionValues?: Record<string, string>;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  structuredData?: object | object[];
}

export function useSEO(config: SEOConfig): React.ReactElement {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as Language;

  const title = t(config.titleKey, config.titleValues);
  const description = t(config.descriptionKey, config.descriptionValues);

  const canonicalPath = localizedPath(config.routeKey, config.routeParams, currentLang);
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;

  const ogImage = config.ogImage || DEFAULT_OG_IMAGE;
  const ogType = config.ogType || 'website';
  const ogLocale = currentLang === 'hu' ? 'hu_HU' : 'en_US';
  const ogLocaleAlternate = currentLang === 'hu' ? 'en_US' : 'hu_HU';

  const languages: Language[] = ['hu', 'en'];
  const hrefLangLinks = languages.map((lang) => ({
    lang,
    href: `${SITE_URL}${localizedPath(config.routeKey, config.routeParams, lang)}`,
  }));

  const structuredDataArray = config.structuredData
    ? Array.isArray(config.structuredData)
      ? config.structuredData
      : [config.structuredData]
    : [];

  return (
      <Helmet>
          <title>{title}</title>
          <meta name="description" content={description}/>

          {config.noIndex && <meta name="robots" content="noindex, nofollow"/>}

          <link rel="canonical" href={canonicalUrl}/>
          {hrefLangLinks.map(({lang, href}) => (
              <link key={lang} rel="alternate" hrefLang={lang} href={href}/>
          ))}
          <link
              rel="alternate"
              hrefLang="x-default"
              href={`${SITE_URL}${localizedPath(config.routeKey, config.routeParams, 'hu')}`}
          />

          <meta name="robots" content="max-image-preview:large"/>

          <meta property="og:title" content={title}/>
          <meta property="og:description" content={description}/>
          <meta property="og:url" content={canonicalUrl}/>
          <meta property="og:image" content={ogImage}/>
          <meta property="og:type" content={ogType}/>
          <meta property="og:locale" content={ogLocale}/>
          <meta property="og:locale:alternate" content={ogLocaleAlternate}/>
          <meta property="og:site_name" content={SITE_NAME}/>

          <meta name="twitter:card" content="summary_large_image"/>
          <meta name="twitter:title" content={title}/>
          <meta name="twitter:description" content={description}/>
          <meta name="twitter:image" content={ogImage}/>

          {structuredDataArray.map((data, index) => (
              <script key={index} type="application/ld+json">
                  {JSON.stringify(data)}
              </script>
          ))}
      </Helmet>
  );
}
